import { Component } from '@angular/core'
import { Checkout, Depot, UserGroup, User } from 'depoto-core/src/entities'
import { CoreService } from '../../../../services'
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { Emittable, Emitter } from '@ngxs-labs/emitter'
import { PlugplayState } from '../../states/plugplay-state.service'
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'app-step3-edit-new-user',
  templateUrl: './step3-edit-new-user.component.html',
  styleUrls: ['./step3-edit-new-user.component.scss'],
})
export class Step3EditNewUserComponent {
  @Emitter(PlugplayState.setActive)
  private readonly setActive: Emittable<number>
  public userForm = new UntypedFormGroup({
    email: new UntypedFormControl({ value: '', disabled: false }, [Validators.required, Validators.email]),
    firstName: new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
    lastName: new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
    phone: new UntypedFormControl({ value: '', disabled: false }),
    // plainPassword: new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
  })
  public depotsGroup: Array<{ id: string; name: string; value: boolean }> = []
  public checkoutsGroup: Array<{ id: string; name: string; value: boolean }> = []
  public userGroups: UserGroup[] = []
  public selectedUserGroups = []
  public isNew
  private userId
  public submit = false
  public isDeletable = true
  public isEmailUnique = true
  password: string = ''
  isPasswordValid = false
  constructor(
    public readonly core: CoreService,
    private route: ActivatedRoute,
    private router: Router,
    private translationService: TranslateService,
  ) {
    this.setActive.emit(2)
    this.userId = this.route.snapshot.params['id']
    this.isNew = this.userId ? false : true
    this.load(this.userId)
    this.getUserRoles()
    // if (!this.isNew) {
    //   //plainPassword: new UntypedFormControl({value: '', disabled: false}, [Validators.required]),
    //   this.userForm.removeControl('plainPassword')
    // }
  }

  private load(id?: string) {
    if (id) {
      this.core.services.user
        .getById(Number(id), {
          email: null,
          firstName: null,
          lastName: null,
          phone: null,
          groups: { id: null, name: null, externalId: null },
          depots: { id: null },
          checkouts: { id: null },
          deletable: null,
        })
        .then(response => {
          this.prepareUserForm(response.firstName, response.lastName, response.email, response.phone)
          this.selectedUserGroups = response.groups.map(g => ({
            ...g,
            externalId: this.translationService.instant(`userGroup.${g.externalId}`),
          }))
          this.depotsGroup = this.prepareDepotsOrCheckouts(this.core.lists.depots, response.depots, false)
          this.checkoutsGroup = this.prepareDepotsOrCheckouts(this.core.lists.checkouts, response.checkouts, false)
          this.isDeletable = response.deletable
        })
    } else {
      this.depotsGroup = this.prepareDepotsOrCheckouts(
        this.core.lists.depots,
        this.core.services.user.user?.depots || [],
        true,
      )
      this.checkoutsGroup = this.prepareDepotsOrCheckouts(
        this.core.lists.checkouts,
        this.core.services.user.user?.checkouts || [],
        true,
      )
    }
  }

  private prepareDepotsOrCheckouts(
    list: Array<{ id; name }>,
    selected: Array<{ id }>,
    isNewUser: boolean,
  ): Array<{ id; name; value }> {
    return list.map(item => {
      return { ...item, value: selected.find(selected => selected.id === item.id) ? !isNewUser : false }
    })
  }

  private prepareUserForm(firstName = '', lastName = '', email = '', phone = ''): void {
    this.userForm.get('email').setValue(email)
    this.userForm.get('firstName').setValue(firstName)
    this.userForm.get('lastName').setValue(lastName)
    this.userForm.get('phone').setValue(phone)
  }

  private getUserRoles(): void {
    this.userGroups = []
    this.core.services.userGroup
      .getList(
        {},
        {
          id: null,
          name: null,
          roles: null,
          externalId: null,
        },
      )
      .then(
        result =>
          (this.userGroups = result.items.map(
            g => new UserGroup({ ...g, externalId: this.translationService.instant(`userGroup.${g.externalId}`) }),
          )),
      )
  }

  async checkIfEmailUnique(): Promise<void> {
    const email = this.userForm.get('email').value
    this.isEmailUnique = await this.core.services.user.isUserEmailUnique(email)
  }

  public create(): void {
    this.core.services.user.create(this.prepareUser()).then(_ => this.router.navigate(['/plugplay/step-3/list']))
  }

  public processSave() {
    this.submit = true
    this.userForm.markAsDirty()
    if (this.userForm.valid && this.isEmailUnique) {
      if (this.isNew) {
        this.create()
      } else {
        this.update()
      }
    }
  }
  public update(): void {
    this.core.services.user.update(this.prepareUser()).then(_ => this.router.navigate(['/plugplay/step-3/list']))
  }

  private prepareUser(): User {
    return new User({
      id: this.userId || undefined,
      email: this.userForm.get('email').value,
      firstName: this.userForm.get('firstName').value,
      lastName: this.userForm.get('lastName').value,
      plainPassword: this.password || '',
      phone: this.userForm.get('phone').value,
      groups: this.userGroups.filter(value => this.selectedUserGroups.includes(value.id)),
      depots: this.depotsGroup.filter(item => item.value).map(item => new Depot({ id: item.id })),
      checkouts: this.checkoutsGroup.filter(item => item.value).map(item => new Checkout({ id: item.id })),
    })
  }

  public depotChanged(id, $event: boolean): void {
    this.depotsGroup = this.depotsGroup.map(item => (item.id !== id ? item : { ...item, value: $event }))
  }

  public checkoutChanged(id, $event: boolean): void {
    this.checkoutsGroup = this.checkoutsGroup.map(item => (item.id !== id ? item : { ...item, value: $event }))
  }

  public rolesChanged($event) {
    this.selectedUserGroups = $event
  }

  public remove(): void {
    this.core.services.user.delete(new User({ id: this.userId }))
  }
}
